/**
 * @class CartController
 * @description Manages the selection of items in a cart, allowing checking, toggling, and retrieving selected items.
 */
export default class CartController {
	/**
	 * @constructor
	 * @param {Object} cartSelection - The cart selection manager instance.
	 */
	constructor(cartSelection) {
		/** @private @type {Object} */
		this._cart = cartSelection;
	}

	/**
	 * Checks if an item with the given type and relationId is currently selected in the cart.
	 * @param {string} type - The type of the item.
	 * @param {number} relationId - The unique ID of the related item.
	 * @returns {boolean} `true` if the item is selected, otherwise `false`.
	 */
	isSelected(type, relationId) {
		const selectedFound = this.find(
			this.cart.selection.value,
			type,
			relationId,
		);
		return !!selectedFound;
	}

	/**
	 * Toggles the selection state of an item in the cart.
	 * If the item is already selected, it gets deselected; otherwise, it gets selected.
	 * @param {string} type - The type of the item.
	 * @param {number} relationId - The unique ID of the related item.
	 */
	toggleSelection(type, relationId) {
		const itemFound = this.find(this.cart.items, type, relationId);
		if (!itemFound) {
			console.log("CartItem not exists with type and relationId");
			return;
		}

		const isSelected = this.isSelected(type, relationId);
		isSelected ? this.cart.deselect(itemFound) : this.cart.select(itemFound);
	}

	/**
	 * Retrieves an item from the cart based on its type and relationId.
	 * @param {string} type - The type of the item.
	 * @param {number} relationId - The unique ID of the related item.
	 * @returns {Object|null} The cart item if found, otherwise `null`.
	 */
	getItem(type, relationId) {
		return this.find(this.cart.items, type, relationId);
	}

	/**
	 * Searches for an item in a given list based on type and relationId.
	 * @private
	 * @param {Array<Object>} items - The list of items to search in.
	 * @param {string} type - The type of the item.
	 * @param {number} relationId - The unique ID of the related item.
	 * @returns {Object|null} The found item or `null` if not found.
	 */
	find(items, type, relationId) {
		return items.find(
			(item) => item.relationType === type && item.relationId === relationId,
		);
	}

	/**
	 * Checks if at least one item in the provided list is selected in the cart.
	 * @param {Array<Object>} items - The list of items to check.
	 * @returns {boolean} `true` if at least one item is selected, otherwise `false`.
	 */
	isOneInCard(items) {
		return items.some((item) => this.isSelected(item.type, item.id));
	}

	/**
	 * Getter method to access the cart selection instance.
	 * @returns {Object} The cart selection instance.
	 */
	get cart() {
		return this._cart;
	}
}
